import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Toaster, toast } from "sonner";
import { ArrowRight, CheckCircle, Storefront, Buildings } from "@phosphor-icons/react";
import { OPPORTUNITY_CATEGORIES, USER_PATHS, TICKET_SIZES } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, required, children, testid }) => (
  <label className="block" data-testid={`${testid}-field`}>
    <span className="block text-[10px] tracking-[0.25em] uppercase text-[#A1AEBF] mb-2">
      {label} {required && <span className="text-[#D1B677]">*</span>}
    </span>
    {children}
  </label>
);

const inputClass =
  "w-full bg-[#02060D] border border-white/10 text-white text-sm px-4 py-3 rounded-none focus:border-[#D1B677] focus:outline-none focus:ring-1 focus:ring-[#D1B677] placeholder:text-[#A1AEBF]/40 transition-colors";

const BusinessForm = () => {
  const [form, setForm] = useState({
    business_name: "",
    contact_person: "",
    email: "",
    phone: "",
    category: OPPORTUNITY_CATEGORIES[0].title,
    city: "",
    website: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads/business`, form);
      toast.success("Welcome to CountGoa — your registration is received.");
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="business-form-success"
        className="border border-[#D1B677]/40 bg-[#D1B677]/[0.04] p-12 text-center"
      >
        <CheckCircle size={48} weight="light" className="text-[#D1B677] mx-auto mb-6" />
        <h3 className="font-serif text-white text-3xl mb-3">You're in.</h3>
        <p className="text-[#A1AEBF] max-w-md mx-auto">
          Your Goa-based business has been registered with CountGoa. Our team will
          reach out for verification within 48 hours.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="business-form"
      className="space-y-5"
      noValidate
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Business Name" required testid="business-name">
          <input
            type="text"
            required
            value={form.business_name}
            onChange={onChange("business_name")}
            placeholder="e.g. Casa Maritima Resorts"
            className={inputClass}
            data-testid="business-name-input"
          />
        </Field>
        <Field label="Contact Person" required testid="contact-person">
          <input
            type="text"
            required
            value={form.contact_person}
            onChange={onChange("contact_person")}
            placeholder="Your full name"
            className={inputClass}
            data-testid="contact-person-input"
          />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Email" required testid="business-email">
          <input
            type="email"
            required
            value={form.email}
            onChange={onChange("email")}
            placeholder="you@business.com"
            className={inputClass}
            data-testid="business-email-input"
          />
        </Field>
        <Field label="Phone" required testid="business-phone">
          <input
            type="tel"
            required
            value={form.phone}
            onChange={onChange("phone")}
            placeholder="+91 ..."
            className={inputClass}
            data-testid="business-phone-input"
          />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Category" required testid="business-category">
          <select
            value={form.category}
            onChange={onChange("category")}
            className={inputClass}
            data-testid="business-category-select"
          >
            {OPPORTUNITY_CATEGORIES.map((c) => (
              <option key={c.key} value={c.title} className="bg-[#050B14]">
                {c.title}
              </option>
            ))}
          </select>
        </Field>
        <Field label="City in Goa" testid="business-city">
          <input
            type="text"
            value={form.city}
            onChange={onChange("city")}
            placeholder="Panaji, Calangute, etc."
            className={inputClass}
            data-testid="business-city-input"
          />
        </Field>
      </div>

      <Field label="Website (optional)" testid="business-website">
        <input
          type="url"
          value={form.website}
          onChange={onChange("website")}
          placeholder="https://"
          className={inputClass}
          data-testid="business-website-input"
        />
      </Field>

      <Field label="Tell us about your business" testid="business-description">
        <textarea
          rows={4}
          value={form.description}
          onChange={onChange("description")}
          placeholder="Brief description, what you offer, growth ambitions..."
          className={`${inputClass} resize-none`}
          data-testid="business-description-input"
        />
      </Field>

      <div className="pt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <p className="text-[11px] tracking-[0.2em] uppercase text-[#D1B677]">
          ✓ FREE Registration · Goa-based businesses only
        </p>
        <button
          type="submit"
          disabled={loading}
          data-testid="business-form-submit"
          className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#D1B677] text-[#02060D] text-sm font-semibold hover:bg-[#E6CC8A] transition-all disabled:opacity-50"
        >
          {loading ? "Submitting..." : "Register Business Free"}
          {!loading && (
            <ArrowRight
              size={18}
              weight="bold"
              className="group-hover:translate-x-1 transition-transform"
            />
          )}
        </button>
      </div>
    </form>
  );
};

const InvestorForm = () => {
  const [form, setForm] = useState({
    full_name: "",
    organization: "",
    email: "",
    phone: "",
    country: "",
    interest: "invest",
    sectors: [],
    ticket_size: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleSector = (s) =>
    setForm((f) => ({
      ...f,
      sectors: f.sectors.includes(s)
        ? f.sectors.filter((x) => x !== s)
        : [...f.sectors, s],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads/investor`, form);
      toast.success("Thank you — our concierge team will be in touch.");
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="investor-form-success"
        className="border border-[#D1B677]/40 bg-[#D1B677]/[0.04] p-12 text-center"
      >
        <CheckCircle size={48} weight="light" className="text-[#D1B677] mx-auto mb-6" />
        <h3 className="font-serif text-white text-3xl mb-3">Inquiry received.</h3>
        <p className="text-[#A1AEBF] max-w-md mx-auto">
          A CountGoa concierge will reach out to schedule a confidential briefing on
          relevant opportunities.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="investor-form"
      className="space-y-5"
      noValidate
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Full Name" required testid="investor-name">
          <input
            type="text"
            required
            value={form.full_name}
            onChange={onChange("full_name")}
            placeholder="Your full name"
            className={inputClass}
            data-testid="investor-name-input"
          />
        </Field>
        <Field label="Organization" testid="investor-org">
          <input
            type="text"
            value={form.organization}
            onChange={onChange("organization")}
            placeholder="Firm / Family Office / Company"
            className={inputClass}
            data-testid="investor-org-input"
          />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Email" required testid="investor-email">
          <input
            type="email"
            required
            value={form.email}
            onChange={onChange("email")}
            placeholder="you@firm.com"
            className={inputClass}
            data-testid="investor-email-input"
          />
        </Field>
        <Field label="Phone" testid="investor-phone">
          <input
            type="tel"
            value={form.phone}
            onChange={onChange("phone")}
            placeholder="+1 ..."
            className={inputClass}
            data-testid="investor-phone-input"
          />
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <Field label="Country" required testid="investor-country">
          <input
            type="text"
            required
            value={form.country}
            onChange={onChange("country")}
            placeholder="e.g. UAE, Singapore, UK"
            className={inputClass}
            data-testid="investor-country-input"
          />
        </Field>
        <Field label="Intent" required testid="investor-interest">
          <select
            value={form.interest}
            onChange={onChange("interest")}
            className={inputClass}
            data-testid="investor-interest-select"
          >
            {USER_PATHS.map((p) => (
              <option key={p.key} value={p.key} className="bg-[#050B14]">
                {p.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Sectors of Interest" testid="investor-sectors">
        <div className="flex flex-wrap gap-2 pt-1">
          {OPPORTUNITY_CATEGORIES.map((c) => {
            const active = form.sectors.includes(c.title);
            return (
              <button
                type="button"
                key={c.key}
                onClick={() => toggleSector(c.title)}
                data-testid={`sector-chip-${c.key}`}
                className={`text-xs px-3 py-2 border transition-colors ${
                  active
                    ? "bg-[#D1B677] text-[#02060D] border-[#D1B677]"
                    : "border-white/10 text-[#A1AEBF] hover:border-[#D1B677]/60 hover:text-white"
                }`}
              >
                {c.title}
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Ticket Size" testid="investor-ticket">
        <select
          value={form.ticket_size}
          onChange={onChange("ticket_size")}
          className={inputClass}
          data-testid="investor-ticket-select"
        >
          <option value="" className="bg-[#050B14]">
            Select range (optional)
          </option>
          {TICKET_SIZES.map((t) => (
            <option key={t} value={t} className="bg-[#050B14]">
              {t}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Message" testid="investor-message">
        <textarea
          rows={4}
          value={form.message}
          onChange={onChange("message")}
          placeholder="Tell us what you're exploring..."
          className={`${inputClass} resize-none`}
          data-testid="investor-message-input"
        />
      </Field>

      <div className="pt-3 flex justify-end">
        <button
          type="submit"
          disabled={loading}
          data-testid="investor-form-submit"
          className="group inline-flex items-center justify-center gap-3 px-8 py-4 border border-[#D1B677] text-[#D1B677] text-sm font-semibold hover:bg-[#D1B677] hover:text-[#02060D] transition-all disabled:opacity-50"
        >
          {loading ? "Submitting..." : "Submit Inquiry"}
          {!loading && (
            <ArrowRight
              size={18}
              weight="bold"
              className="group-hover:translate-x-1 transition-transform"
            />
          )}
        </button>
      </div>
    </form>
  );
};

export const LeadForms = () => {
  const [tab, setTab] = useState("business");

  return (
    <section
      id="register"
      data-testid="register-section"
      className="relative bg-[#050B14] py-24 md:py-32 border-t border-white/5"
    >
      <Toaster theme="dark" position="top-right" richColors />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-16">
          {/* Left: copy */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-xs tracking-[0.3em] uppercase text-[#D1B677] font-medium">
                Begin Here
              </span>
            </div>
            <h2 className="font-serif text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] mb-8">
              Two paths.{" "}
              <span className="italic text-[#D1B677]">One coastal gateway.</span>
            </h2>
            <p className="text-[#A1AEBF] text-base leading-relaxed mb-12 max-w-md">
              Whether you're a Goa-based founder taking your venture global, or a
              global partner looking inward — your access begins below.
            </p>

            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setTab("business")}
                data-testid="tab-business"
                className={`w-full flex items-start gap-4 p-5 border text-left transition-all ${
                  tab === "business"
                    ? "border-[#D1B677] bg-[#D1B677]/[0.06]"
                    : "border-white/8 hover:border-white/20"
                }`}
              >
                <Storefront
                  size={28}
                  weight="light"
                  className={tab === "business" ? "text-[#D1B677]" : "text-[#A1AEBF]"}
                />
                <div className="flex-1">
                  <div className="font-serif text-white text-lg mb-1">
                    Goa Business · FREE
                  </div>
                  <div className="text-xs text-[#A1AEBF] leading-relaxed">
                    Register your Goa-based venture for global exposure.
                  </div>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setTab("investor")}
                data-testid="tab-investor"
                className={`w-full flex items-start gap-4 p-5 border text-left transition-all ${
                  tab === "investor"
                    ? "border-[#D1B677] bg-[#D1B677]/[0.06]"
                    : "border-white/8 hover:border-white/20"
                }`}
              >
                <Buildings
                  size={28}
                  weight="light"
                  className={tab === "investor" ? "text-[#D1B677]" : "text-[#A1AEBF]"}
                />
                <div className="flex-1">
                  <div className="font-serif text-white text-lg mb-1">
                    Investor · Partner · Enterprise
                  </div>
                  <div className="text-xs text-[#A1AEBF] leading-relaxed">
                    Explore opportunities to invest, partner, trade or expand in Goa.
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="border border-white/8 bg-[#02060D] p-8 lg:p-12"
            >
              {tab === "business" ? <BusinessForm /> : <InvestorForm />}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadForms;
